import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { createPatientApi } from "../../../../api-helpers/PatientApiHelper";
import { ToastConfig } from "../../../../components/Toaster";
import { GetPatientDetails, PatientPersonalInfo, UpdateProgramAndPathway } from "patients-experience-client";
import Spinner from "../../../../components/Spinner";
import { LookupDropdown } from "../../../../components/DropDown/LookupDropdown";
import Lookups from "../../../../constants/Lookups";
import { WorkflowConstants } from "../../../../helper/WorkflowConstants";
import { WorkflowStatusDropdown } from "../../../../components/DropDown/WorkflowStatusDropdown";
import { WorkflowState } from "common-util-experience-client";
import { Lookup } from "../../../../interfaces/allergy";
import { createCommonUtilsApi } from "../../../../api-helpers/CommonUtilsApiHelper";
import { useDispatch, useSelector } from "react-redux";
import { setPatientProfileReloadKey } from "../../../../store/slices/patientProfileReload";
import { RootState } from "../../../../store";

interface PathwayProps {
    setOffCanvasShow: (show: boolean) => void;
    programPathway: GetPatientDetails | undefined;
    patientInfo: PatientPersonalInfo | undefined;
    onSubmit: (toasterConfig: ToastConfig) => void;
}

export const EditPathway: React.FC<PathwayProps> = (props: PathwayProps) => {
    const [loading, setloading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [programType, setProgramType] = useState<string>('');
    const patientApi = createPatientApi();
    const [programs, setPrograms] = useState<Lookup[]>([]);
    const [form, setForm] = useState<UpdateProgramAndPathway>({
        programId: props.programPathway?.program?.id || '',
        comment: props.programPathway?.program?.comment || '',
        pathwayWfStateId: props.programPathway?.pathway?.pathwayWfStateId || '',
        currentPathway: props.programPathway?.pathway?.currentPathway || '',
    });

    const lookupApi = createCommonUtilsApi()
    const dispatch = useDispatch();
    const patientProfileReloadKey = useSelector((state: RootState) => state.patientProfileReloadKey.patientProfileReloadKey);

    const onProgramUpdate = (program: Lookup | undefined) => {
        setForm({
            ...form,
            programId: program?.id!
        });
        setProgramType(program?.name!)
    }

    
    const getPrograms = async () => {
        try {
            const response= await lookupApi.lookupControllerFindAll(Lookups.PROGRAM_TYPES);
            setPrograms(response.data);
        } catch (error) {
        console.log(error);
        } 
    };

    const onPathwayUpdate = (state: WorkflowState) => {
        setForm({
            ...form,
            pathwayWfStateId: state.stateId,
            currentPathway: state.stateName
        });
    }

    const submitHandler = async (event: any) => {
        event.preventDefault();
        setValidated(true);

        const program = programs.find(p => p.id === form.programId)?.name
        let formBody = {...form}

        if (program !== 'Other'){
           formBody = {
             ...form,
              comment: ''
           }
           setForm(formBody)
        }
        
        const currentForm = event.currentTarget;
        if (!currentForm.checkValidity()) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        
        setloading(true);
        try {
            await patientApi.patientsControllerUpdateProgramAndPathway(formBody, props.patientInfo?.patientId!);
            const toasterConfig = {
                type: "success",
                title: "Wellinks Program & Pathway updated successfully!",
                icon: "check_circle_outline",
            };
            props.onSubmit(toasterConfig as ToastConfig);
            setTimeout(() => {
                handleDiscard();
                
            }, 1000);
        } catch (error) {
            const toasterConfig = {
                type: "danger",
                title: "Error in updating Wellinks Program & Pathway!",
                icon: "error_outline",
            };
            props.onSubmit(toasterConfig as ToastConfig);
        } finally {
            setloading(false);
            setTimeout(()=>{
                dispatch(setPatientProfileReloadKey(patientProfileReloadKey + 1))
            },1500)
        }
    };

    const handleDiscard = () => {
        props.setOffCanvasShow(false);
    };

    useEffect(() => {
        getPrograms();
    }, []);

    return (
        <>
        {loading && <Spinner />}
        <Form
            noValidate
            validated={validated}
            onSubmit={submitHandler}
            className="d-flex flex-column h-100">
            <div className="flex-fill">
                <div className="row g-2 mb-3">
                    <div className="col-12">
                        <div className="mb-3">
                            <label
                                className="form-label fs-12 fw-semibold mb-1"
                            >
                                Program
                            </label>
                            <LookupDropdown
                                label="Program"
                                initialValue={form.programId}
                                type={Lookups.PROGRAM_TYPES}
                                onChange={(program) => onProgramUpdate(program)}
                                disabled={false}
                                required={true}
                            />
                        </div>
                    </div>
                    {
                        programType?.toLocaleLowerCase() === 'other' && <div className="col-12">
                            <div className="mb-3">
                                <label
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    Comment
                                </label>
                                <textarea name="comment" required={programType?.toLocaleLowerCase() === 'other'}
                                    onChange={(e) => setForm({
                                    ...form,
                                    comment: e.target.value
                                })} value={form.comment} className="form-control" rows={3}></textarea>
                            </div>
                        </div>
                    }
                    <div className="col-12">
                        <div className="mb-3">
                            <label
                                className="form-label fs-12 fw-semibold mb-1"
                            >
                                Pathway
                            </label>
                            <WorkflowStatusDropdown
                                label="Pathway"
                                initialValue={form.pathwayWfStateId}
                                type={WorkflowConstants.pathway}
                                onChange={(pathway) => onPathwayUpdate(pathway!)}
                                disabled={false}
                                required={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="offcanvas-footer text-end w-100 border-top d-flex justify-content-between border-top">
                <div>
                    <button
                        className="btn btn-outline-secondary rounded-pill px-5 fs-13"
                        type="reset"
                        onClick={handleDiscard}
                    >
                        Discard
                    </button>
                </div>
                <div>
                    <button
                        className="btn btn-secondary rounded-pill ps-4 pe-5 fs-13"
                        type="submit"
                    >
                        Update
                    </button>
                </div>
            </div>
        </Form>
        </>
    );
};